import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { requireAdminOrDeny } from "@/lib/admin-guard";
import {
  listAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
} from "@/lib/admin-notifications.functions";
import { Bell, Check, CheckCheck, ArrowLeft, AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

export const Route = createFileRoute("/admin_/notifications")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Notifications — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdminOrDeny,
  component: NotificationsPage,
});

type Notification = {
  id: string;
  created_at: string;
  title: string;
  body: string | null;
  severity: "info" | "success" | "warning" | "error";
  kind: string | null;
  related_id: string | null;
  url: string | null;
  read_at: string | null;
  metadata: Record<string, unknown> | null;
};

function severityIcon(s: Notification["severity"]) {
  const cls = "h-4 w-4 shrink-0";
  if (s === "error") return <AlertCircle className={`${cls} text-red-500`} />;
  if (s === "warning") return <AlertTriangle className={`${cls} text-amber-500`} />;
  if (s === "success") return <CheckCircle2 className={`${cls} text-emerald-500`} />;
  return <Info className={`${cls} text-sky-500`} />;
}

function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useServerFn(listAdminNotifications);
  const markOne = useServerFn(markAdminNotificationRead);
  const markAll = useServerFn(markAllAdminNotificationsRead);
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-notifications", { unreadOnly }],
    queryFn: () => list({ data: { unreadOnly } }),
    refetchInterval: 15_000,
  });

  const markOneMut = useMutation({
    mutationFn: (id: string) => markOne({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-notifications"] }),
    onError: (e: any) => toast.error(e?.message ?? "Failed to mark read"),
  });

  const markAllMut = useMutation({
    mutationFn: () => markAll({}),
    onSuccess: () => {
      toast.success("All notifications marked as read");
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to mark all read"),
  });

  const notifications = (data?.notifications ?? []) as Notification[];
  const unread = data?.unreadCount ?? 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
          <span className="text-muted-foreground">/</span>
          <h1 className="font-display text-2xl text-foreground flex items-center gap-2">
            <Bell className="h-6 w-6 text-[var(--gold-deep)]" />
            Notifications
            {unread > 0 && (
              <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                {unread}
              </span>
            )}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => markAllMut.mutate()}
          disabled={markAllMut.isPending || unread === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-gold/20 bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-gold/10 disabled:opacity-40"
        >
          <CheckCheck className="h-4 w-4" /> Mark all read
        </button>
      </div>

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={unreadOnly}
          onChange={(e) => setUnreadOnly(e.target.checked)}
        />
        Show unread only
      </label>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && (
        <p className="text-sm text-red-500">
          Failed to load: {(error as Error).message}{" "}
          <button className="underline" onClick={() => refetch()}>retry</button>
        </p>
      )}

      {!isLoading && notifications.length === 0 && (
        <div className="rounded-2xl border border-gold/20 bg-card p-8 text-center text-sm text-muted-foreground">
          No notifications yet. Broadcast completions and stop-alerts will appear here.
        </div>
      )}

      <ul className="space-y-2">
        {notifications.map((n) => {
          const isUnread = !n.read_at;
          return (
            <li
              key={n.id}
              className={`rounded-xl border p-4 transition ${
                isUnread
                  ? "border-gold/40 bg-gold/5"
                  : "border-border bg-card"
              }`}
            >
              <div className="flex items-start gap-3">
                {severityIcon(n.severity)}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`text-sm ${isUnread ? "font-semibold" : "font-medium"} text-foreground`}>
                      {n.title}
                    </h3>
                    <time className="shrink-0 text-xs text-muted-foreground">
                      {new Date(n.created_at).toLocaleString()}
                    </time>
                  </div>
                  {n.body && (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                      {n.body}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    {n.url && (
                      <a
                        href={n.url}
                        className="text-[var(--gold-deep)] hover:underline"
                      >
                        Open →
                      </a>
                    )}
                    {isUnread && (
                      <button
                        type="button"
                        onClick={() => markOneMut.mutate(n.id)}
                        disabled={markOneMut.isPending}
                        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                      >
                        <Check className="h-3 w-3" /> Mark read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
